import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ArrowRight, ArrowLeft, X, Plus, MagnifyingGlass } from "@phosphor-icons/react";

// Renders the Dependencies cell: shows existing structured dep_links + legacy free-text deps as badges,
// and a + button that opens a popover to pick from other tasks/subtasks with blocks/blocked-by direction.
export default function DependencyPicker({
  task,
  allTasks,
  numberingById,
  canEdit,
  onChange,
  width,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pendingKind, setPendingKind] = useState("blocked_by");

  const links = task.dependency_links || [];
  const legacyText = (task.dependencies || "").trim();

  const candidates = useMemo(() => {
    const linkedIds = new Set(links.map((l) => l.target_id));
    const q = query.trim().toLowerCase();
    return allTasks
      .filter((t) => t.id !== task.id && !linkedIds.has(t.id))
      .filter((t) => {
        if (!q) return true;
        const name = (t.task || t.subtask || "").toLowerCase();
        const num = numberingById.get(t.id) || "";
        return name.includes(q) || num.includes(q);
      })
      .slice(0, 30);
  }, [allTasks, task.id, links, query, numberingById]);

  const addLink = (target_id, kind) => {
    const next = [...links, { target_id, kind }];
    onChange({ dependency_links: next });
    setQuery("");
  };

  const removeLink = (target_id) => {
    const next = links.filter((l) => l.target_id !== target_id);
    onChange({ dependency_links: next });
  };

  const updateLegacyText = (v) => {
    onChange({ dependencies: v });
  };

  return (
    <div
      className="flex items-center gap-1 px-2 border-r border-slate-100 shrink-0 h-full"
      style={{ width }}
      data-testid={`cell-deps-${task.id}`}
    >
      <div className="flex items-center gap-1 flex-1 min-w-0 overflow-hidden">
        {links.length === 0 && !legacyText && (
          <span className="text-slate-400 text-xs">—</span>
        )}
        {links.slice(0, 2).map((l) => {
          const target = allTasks.find((t) => t.id === l.target_id);
          const num = numberingById.get(l.target_id) || "?";
          const name = target ? (target.task || target.subtask || "Untitled") : "(missing)";
          const Icon = l.kind === "blocks" ? ArrowRight : ArrowLeft;
          const tone = l.kind === "blocks"
            ? "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/40 dark:border-amber-800/40 dark:text-amber-300"
            : "bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-950/40 dark:border-rose-800/40 dark:text-rose-300";
          return (
            <span
              key={l.target_id}
              className={`inline-flex items-center gap-1 h-5 px-1.5 rounded-md border text-[10px] font-medium font-mono-ibm whitespace-nowrap min-w-0 max-w-[120px] shrink ${tone}`}
              title={`${l.kind === "blocks" ? "Blocks" : "Blocked by"} ${num} · ${name}`}
              data-testid={`dep-link-${task.id}-${l.target_id}`}
            >
              <Icon size={9} weight="bold" className="shrink-0" />
              <span className="truncate">{name}</span>
              {canEdit && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeLink(l.target_id); }}
                  className="hover:text-red-600 -mr-0.5 shrink-0"
                  title="Remove dependency"
                  data-testid={`dep-link-remove-${task.id}-${l.target_id}`}
                >
                  <X size={9} weight="bold" />
                </button>
              )}
            </span>
          );
        })}
        {links.length > 2 && (
          <span
            className="text-[10px] font-mono-ibm text-slate-500 shrink-0"
            title={links.slice(2).map((l) => {
              const t = allTasks.find((x) => x.id === l.target_id);
              return `${l.kind === "blocks" ? "→" : "←"} ${t ? (t.task || t.subtask) : "?"}`;
            }).join("\n")}
          >
            +{links.length - 2}
          </span>
        )}
      </div>

      {canEdit && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="h-5 w-5 rounded-md border border-dashed border-slate-300 hover:border-slate-950 hover:bg-slate-950 hover:text-white text-slate-500 flex items-center justify-center shrink-0 transition-colors"
              data-testid={`dep-add-${task.id}`}
              title="Link a dependency"
            >
              <Plus size={10} weight="bold" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-[340px] p-0 rounded-md border border-slate-300 shadow-[0_12px_40px_rgba(15,23,42,0.15)]"
            data-testid={`dep-picker-${task.id}`}
          >
            <div className="p-2 border-b border-slate-200">
              <div className="text-[10px] uppercase tracking-widest font-mono-ibm text-slate-500 mb-1.5">
                Direction
              </div>
              <div className="flex gap-1 mb-2">
                <button
                  type="button"
                  onClick={() => setPendingKind("blocked_by")}
                  className={`flex-1 h-7 rounded-md text-[11px] font-medium border flex items-center justify-center gap-1.5 ${
                    pendingKind === "blocked_by"
                      ? "bg-rose-50 border-rose-300 text-rose-800"
                      : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                  }`}
                  data-testid={`dep-kind-blocked_by-${task.id}`}
                >
                  <ArrowLeft size={11} weight="bold" /> Blocked by
                </button>
                <button
                  type="button"
                  onClick={() => setPendingKind("blocks")}
                  className={`flex-1 h-7 rounded-md text-[11px] font-medium border flex items-center justify-center gap-1.5 ${
                    pendingKind === "blocks"
                      ? "bg-amber-50 border-amber-300 text-amber-800"
                      : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                  }`}
                  data-testid={`dep-kind-blocks-${task.id}`}
                >
                  <ArrowRight size={11} weight="bold" /> Blocks
                </button>
              </div>
              <div className="relative">
                <MagnifyingGlass size={12} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search task or # …"
                  className="h-8 pl-7 pr-2 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950 text-xs"
                  data-testid={`dep-search-${task.id}`}
                />
              </div>
            </div>
            <div className="max-h-[260px] overflow-y-auto custom-scrollbar">
              {candidates.length === 0 ? (
                <div className="p-4 text-center text-xs text-slate-500">
                  No matching tasks.
                </div>
              ) : (
                candidates.map((t) => {
                  const num = numberingById.get(t.id) || "?";
                  const name = t.task || t.subtask || "Untitled";
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => addLink(t.id, pendingKind)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-center gap-2"
                      data-testid={`dep-candidate-${task.id}-${t.id}`}
                    >
                      <span className="text-[10px] font-mono-ibm text-slate-500 w-9 shrink-0">
                        {num}
                      </span>
                      <span className="text-xs text-slate-800 truncate flex-1">
                        {name}
                      </span>
                      {t.parent_id && (
                        <span className="text-[9px] font-mono-ibm uppercase tracking-widest text-slate-400 shrink-0">
                          subtask
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
            {legacyText !== "" || links.length === 0 ? (
              <div className="p-2 border-t border-slate-200 bg-slate-50">
                <div className="text-[10px] uppercase tracking-widest font-mono-ibm text-slate-500 mb-1">
                  Free-text fallback (legacy)
                </div>
                <Input
                  value={legacyText}
                  onChange={(e) => updateLegacyText(e.target.value)}
                  placeholder="e.g. 1, 3, 5"
                  className="h-7 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950 text-[11px] font-mono-ibm"
                  data-testid={`dep-legacy-${task.id}`}
                />
              </div>
            ) : null}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
